import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Search,
  Database,
  CheckCircle,
  AlertCircle,
  ExternalLink,
  Zap,
  ArrowRight,
} from "lucide-react";
import axios from "../config/axios";
import toast from "react-hot-toast";

/**
 * Connectors Page
 * Displays available data source connectors and allows users to connect them
 */
const Connectors = () => {
  const navigate = useNavigate();
  const [connectors, setConnectors] = useState([]);
  const [featuredConnectors, setFeaturedConnectors] = useState([]);
  const [userSources, setUserSources] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchConnectors();
    fetchUserSources();
  }, []);

  const fetchConnectors = async () => {
    try {
      const response = await axios.get(`/api/connectors`);

      setConnectors(response.data.connectors);
      setFeaturedConnectors(response.data.featured);
    } catch (error) {
      console.error("Failed to fetch connectors:", error);
      toast.error("Failed to load connectors");
    } finally {
      setLoading(false);
    }
  };

  const fetchUserSources = async () => {
    try {
      const response = await axios.get(`/api/connectors/user/sources`);

      setUserSources(response.data.sources);
    } catch (error) {
      console.error("Failed to fetch user sources:", error);
    }
  };

  const handleConnectorClick = (connector) => {
    if (connector.connected) {
      // Navigate to datasource management page using instance ID
      // Need to find the instance ID from userSources
      const userSource = userSources.find(
        (s) => s.connectorId === connector.id,
      );
      if (userSource) {
        window.location.href = `/datasources/${userSource.id}`;
      } else {
        // Fallback to setup if no instance found
        window.location.href = `/connectors/${connector.id}/setup`;
      }
    } else {
      // Navigate to setup flow
      window.location.href = `/connectors/${connector.id}/setup`;
    }
  };

  // Filter connectors
  const filteredConnectors = connectors.filter((connector) => {
    const matchesSearch = connector.name
      .toLowerCase()
      .includes(searchTerm.toLowerCase());
    const matchesCategory =
      selectedCategory === "all" || connector.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  // Get unique categories
  const categories = ["all", ...new Set(connectors.map((c) => c.category))];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          Supported Sources
        </h1>
        <p className="text-gray-600">
          Connect your marketing, analytics, and business data sources to
          power your insights
        </p>
      </div>

      {/* Connectors Grid */}
      {filteredConnectors.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <Database className="w-12 h-12 text-gray-400 mx-auto mb-3" />
          <p className="text-gray-600">No connectors found</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
          {filteredConnectors.map((connector) => (
            <ConnectorCard
              key={connector.id}
              connector={connector}
              onClick={() => handleConnectorClick(connector)}
            />
          ))}
        </div>
      )}
    </div>
  );
};

/**
 * Connector Card Component
 */
const ConnectorCard = ({ connector, onClick }) => {
  return (
    <div
      onClick={onClick}
      className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-lg transition-shadow cursor-pointer group"
    >
      <div className="flex items-start justify-between mb-3">
        <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center group-hover:bg-primary-50 transition-colors p-2">
          {connector.iconUrl ? (
            <img
              src={connector.iconUrl}
              alt={`${connector.name} logo`}
              className="w-full h-full object-contain"
              onError={(e) => {
                e.target.style.display = "none";
                e.target.nextSibling.style.display = "block";
              }}
            />
          ) : null}
          <Database
            className="w-6 h-6 text-gray-600 group-hover:text-primary-600"
            style={{ display: connector.iconUrl ? "none" : "block" }}
          />
        </div>

        {connector.connected ? (
          <div className="flex items-center text-xs font-medium text-green-600 bg-green-50 px-2 py-1 rounded">
            <CheckCircle className="w-3 h-3 mr-1" />
            Connected
          </div>
        ) : (
          <div className="text-xs font-medium text-gray-500 bg-gray-100 px-2 py-1 rounded">
            {connector.supportLevel === "certified" ? "Certified" : "Beta"}
          </div>
        )}
      </div>

      <h3 className="font-semibold text-gray-900 mb-1">{connector.name}</h3>

      <p className="text-xs text-gray-500 mb-3 line-clamp-2">
        {connector.description || `Connect your ${connector.name} data`}
      </p>

      <div className="flex items-center justify-between">
        <span className="text-xs text-gray-500">{connector.category}</span>
        <ExternalLink className="w-4 h-4 text-gray-400 group-hover:text-primary-600" />
      </div>
    </div>
  );
};

export default Connectors;
